/**
 * Per-tab latch recording that this tab cannot reach DUST at all.
 *
 * A fetch-level SSE failure — the request never produces an HTTP response at
 * all (DNS failure, TLS error, CORS block, connection refused) — is
 * qualitatively different from the failures DustSseEngine is built to ride out.
 * A 503 or a DDR proves DUST answered and asked us to come back; a fetch
 * rejection means nothing on the path to DUST is answering, which in practice
 * is a proxy, firewall, or DNS policy that will not resolve itself while the tab
 * is open. The engine keeps retrying either way — it cannot tell a dead path
 * from a flapping one, and a client with no alternative is better off waiting —
 * but a caller that has a second working transport should stop waiting on it.
 *
 * Once `consecutiveFetchFailureLimit` such failures happen back-to-back with no
 * successful connection in between, this latch trips. Operator reads it (via
 * useIsDustUnreachable, inside useOperatorStreamingTransport) to pin both its
 * event subscription and its request transport to Pusher.
 *
 * The verdict expires after `dustUnreachableTtlMs` so a network path that has
 * since been fixed is picked back up. Expiry is driven by a timer rather than
 * computed on read, for two reasons. Readers are React hooks that re-render only
 * when notified, so a read-time expiry would go unnoticed in an idle tab. And a
 * read-time expiry could lapse between two readers in a single render pass,
 * leaving Operator listening on DUST while its request sender still believed
 * Pusher — publishing where nobody is subscribed. If DUST is still unreachable
 * when the verdict lapses, the next fetch failure re-latches immediately: the
 * engine has been counting the whole time, so the streak is already well past
 * the limit.
 *
 * The verdict is persisted in sessionStorage so a reload does not have to spend
 * another round of failing connection attempts re-deriving it. sessionStorage is
 * per-tab, and its copy-into-duplicated-tabs behavior is harmless here — unlike
 * getDustTabSessionId, where a shared value would collide, a duplicated tab
 * inherits a correct verdict about a network path it also shares.
 */

import { SessionStorageManager, sessionStorageKeys } from "@lib/sessionStorageManager";

import { dustUnreachableTtlMs } from "./constants";

type DustUnreachableListener = () => void;

const listeners = new Set<DustUnreachableListener>();

// Epoch ms at which the latch tripped, mirrored from sessionStorage so reads are
// synchronous and allocation-free. `null` means "read, and this tab holds no
// live verdict"; `undefined` means "not yet read".
let latchedAt: number | null | undefined;

let expiryTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Arms the one-shot timer that retires a verdict once its TTL runs out. Every
 * reader learns about the expiry from the same notification, so they cannot
 * disagree about the transport while the flip is in progress.
 *
 * @param at Epoch ms at which the verdict was recorded.
 */
const scheduleExpiry = (at: number): void => {
  if (expiryTimeoutId !== null) {
    return;
  }

  const remainingMs = Math.max(0, at + dustUnreachableTtlMs - Date.now());

  expiryTimeoutId = setTimeout(() => {
    expiryTimeoutId = null;
    latchedAt = null;
    SessionStorageManager.remove(sessionStorageKeys.DUST_UNREACHABLE_IN_TAB);
    listeners.forEach((listener) => listener());
  }, remainingMs);
};

/**
 * Whether this tab currently holds a live verdict that it cannot reach DUST.
 *
 * @return true while the latch is tripped and its TTL has not lapsed
 */
export const isDustUnreachableInTab = (): boolean => {
  if (latchedAt === undefined) {
    const storedAt = SessionStorageManager.get<number>(sessionStorageKeys.DUST_UNREACHABLE_IN_TAB);
    latchedAt = null;

    if (typeof storedAt === "number") {
      if (Date.now() - storedAt < dustUnreachableTtlMs) {
        latchedAt = storedAt;
        scheduleExpiry(storedAt);
      } else {
        SessionStorageManager.remove(sessionStorageKeys.DUST_UNREACHABLE_IN_TAB);
      }
    }
  }

  return latchedAt !== null;
};

/**
 * Trips the latch and notifies subscribers. A call made while a verdict is
 * already live is a no-op — it neither re-notifies nor extends the TTL.
 */
export const latchDustUnreachable = (): void => {
  if (isDustUnreachableInTab()) {
    return;
  }

  const now = Date.now();
  latchedAt = now;
  SessionStorageManager.set(sessionStorageKeys.DUST_UNREACHABLE_IN_TAB, now);
  scheduleExpiry(now);
  listeners.forEach((listener) => listener());
};

/**
 * Subscribes to the latch changing — once when it trips, and again when the
 * verdict expires.
 *
 * @param listener Called after each change.
 * @return Unsubscribe function.
 */
export const subscribeToDustUnreachable = (listener: DustUnreachableListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Drops the in-memory mirror and the pending expiry timer so the next read
 * consults sessionStorage again, leaving the stored verdict untouched. Intended
 * for tests, which use it to simulate a fresh page load in the same tab.
 */
export const resetDustUnreachableLatchCache = (): void => {
  latchedAt = undefined;

  if (expiryTimeoutId !== null) {
    clearTimeout(expiryTimeoutId);
    expiryTimeoutId = null;
  }
};
